import { FC, useEffect, useRef, useState } from "react";
import { TextInput } from "react-native";
import { XIcon } from "lucide-react-native";
import { useFormContext } from "react-hook-form";

import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnButtonSelectInput } from "@/components/VnButtonSelectInput/VnButtonSelectInput";
import { VnCheckboxInput } from "@/components/VnCheckboxInput/VnCheckboxInput";
import { VnDateOfBirthInput } from "@/components/VnDateOfBirthInput";
import { VnFormLabel } from "@/components/VnForm";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { VnTouchableOpacity } from "@/components/VnTouchableOpacity";
import {
  HouseholdMemberAgeRangeType,
  householdMemberAges,
  HouseholdMemberAndRelationshipFormType,
  HouseholdMemberRelationsType,
  HouseholdMemberWithRelationshipType,
} from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate.ts";
import { ssnMask } from "@/utils/string";

import { HouseholdMemberRelationshipForm } from "./HouseholdMemberRelationshipForm";
import { SecureTextEntryToggle } from "./SecureTextEntryToggle";

export type HouseholdMemberAndRelationFormParamsType = {
  relationshipOptions?: HouseholdMemberRelationsType[];
  ageRangeOptions?: HouseholdMemberAgeRangeType[];
  assumptions?: Partial<
    Omit<HouseholdMemberAndRelationshipFormType, "householdMemberRelationship">
  > & {
    householdMemberRelationship?: Partial<
      HouseholdMemberAndRelationshipFormType["householdMemberRelationship"]
    >;
  };
};

type HouseholdMemberAndRelationFormPropsType = {
  householdMember?: HouseholdMemberWithRelationshipType;
  isLoading: boolean | undefined;
  params?: HouseholdMemberAndRelationFormParamsType;
  onChangeDateOfBirthState?: (shouldShow: boolean) => void;
};

export const HouseholdMemberAndRelationForm: FC<
  HouseholdMemberAndRelationFormPropsType
> = ({ householdMember, isLoading, params, onChangeDateOfBirthState }) => {
  const { t } = useTranslator();
  const { control, setValue, reset, resetField } =
    useFormContext<HouseholdMemberAndRelationshipFormType>();

  const firstNameRef = useRef<TextInput>(null);
  const lastNameRef = useRef<TextInput>(null);
  const ssnRef = useRef<TextInput>(null);
  const medicaidSubscriberIdRef = useRef<TextInput>(null);

  const [shouldShowDobInput, setShouldShowDobInput] = useState(false);
  const [isSsnVisible, setIsSsnVisible] = useState(false);

  const ageRangeOptions = params?.ageRangeOptions?.length
    ? householdMemberAges.filter(option =>
        params.ageRangeOptions?.some(
          availableOption => availableOption === option.value,
        ),
      )
    : householdMemberAges;

  useEffect(() => {
    if (householdMember && !isLoading) {
      setValue("firstName", householdMember.firstName ?? "");
      setValue("lastName", householdMember.lastName ?? "");
      setValue(
        "householdMemberRelationship.livingWithStatus",
        householdMember.householdMemberRelationship?.livingWithStatus ===
          "LIVING_WITH",
      );

      if (householdMember.householdMemberRelationship?.relationship) {
        setValue(
          "householdMemberRelationship.relationship",
          householdMember.householdMemberRelationship.relationship,
        );
      }
      setValue(
        "householdMemberRelationship.otherRelationship",
        householdMember.householdMemberRelationship?.otherRelationship ?? "",
      );
      if (householdMember.dateOfBirth) {
        setShouldShowDobInput(true);
        setValue("dateOfBirth", householdMember.dateOfBirth);
      } else if (householdMember.ageRange) {
        setShouldShowDobInput(false);
        setValue("ageRange", householdMember.ageRange);
      }

      setValue("ssn", householdMember.ssn ?? "");
      setValue(
        "medicaidSubscriberId",
        householdMember.medicaidSubscriberId ?? "",
      );
    }
  }, [householdMember, setValue, isLoading, reset]);

  useEffect(() => {
    onChangeDateOfBirthState?.(shouldShowDobInput);
  }, [shouldShowDobInput, onChangeDateOfBirthState]);

  return (
    <>
      <VnTextInput
        control={control}
        name="firstName"
        labelI18nKey="native:addHouseholdMemberScreen.firstName"
        placeholderI18nKey="native:addHouseholdMemberScreen.firstName"
        ref={firstNameRef}
        returnKeyType="next"
        onSubmitEditing={() => lastNameRef.current?.focus()}
      />
      <VnTextInput
        control={control}
        name="lastName"
        labelI18nKey="native:addHouseholdMemberScreen.lastName"
        placeholderI18nKey="native:addHouseholdMemberScreen.lastName"
        ref={lastNameRef}
      />
      <VnCheckboxInput
        control={control}
        name="householdMemberRelationship.livingWithStatus"
        option={{
          label: t("native:addHouseholdMemberScreen.livingWithMe"),
        }}
      />
      <VnCheckboxInput
        control={control}
        name="sameMailingAddress"
        option={{
          label: t("native:addHouseholdMemberScreen.sameMailingAddress"),
        }}
      />

      {/* Household Member Relationship */}
      <HouseholdMemberRelationshipForm
        relationshipOptions={params?.relationshipOptions}
      />

      {/* Age Range */}
      <VnBox className="gap-2">
        <VnFormLabel i18nKey="native:addHouseholdMemberScreen.age" isRequired />
        {shouldShowDobInput ? (
          <>
            <VnFormLabel i18nKey="common:dateOfBirth" />
            <VnBox className="flex flex-row items-center">
              <VnBox className="flex-1">
                <VnDateOfBirthInput name="dateOfBirth" control={control} />
              </VnBox>
              <VnTouchableOpacity
                className="px-3.5"
                onPress={() => {
                  setShouldShowDobInput(false);
                  resetField("dateOfBirth");
                }}>
                <VnIcon as={XIcon} />
              </VnTouchableOpacity>
            </VnBox>
          </>
        ) : (
          <>
            <VnButtonSelectInput
              control={control}
              name="ageRange"
              options={ageRangeOptions}
              isRequired={false}
              buttonsClassName="justify-start"
            />
            <VnButton
              i18nKey="native:addHouseholdMemberScreen.enterDateOfBirth"
              variant="link"
              className="justify-start px-0"
              onPress={() => {
                resetField("ageRange");
                setShouldShowDobInput(true);
              }}
            />
          </>
        )}
      </VnBox>

      {/* Coverage Check */}
      <VnBox className="bg-secondary/50 border-border mt-4 gap-4 rounded-xl border p-4">
        <VnBox className="flex-row items-center gap-2">
          <VnBox className="h-3 w-3 rounded-full bg-red-600" />
          <VnText
            variant="lgBold"
            i18nKey="native:addHouseholdMemberScreen.coverageCheck.title"
          />
        </VnBox>
        <VnText
          className="text-muted-foreground mr-4"
          i18nKey="native:addHouseholdMemberScreen.coverageCheck.description"
        />

        {!shouldShowDobInput && (
          <VnBox className="gap-2">
            <VnFormLabel
              i18nKey="native:addHouseholdMemberScreen.coverageCheck.dateOfBirth"
              isRequired={false}
            />
            <VnDateOfBirthInput name="dateOfBirth" control={control} />
          </VnBox>
        )}

        <VnTextInput
          name="ssn"
          control={control}
          labelI18nKey="native:addHouseholdMemberScreen.coverageCheck.ssn"
          placeholderI18nKey="native:addHouseholdMemberScreen.coverageCheck.ssnPlaceholder"
          mask={{
            maskGenerator: ssnMask,
          }}
          isRequired={false}
          returnKeyType="next"
          ref={ssnRef}
          onSubmitEditing={() => medicaidSubscriberIdRef.current?.focus()}
          secureTextEntry={!isSsnVisible}
          keyboardType="numeric"
          isPasswordField={false}
          rightElement={
            <SecureTextEntryToggle
              isVisible={isSsnVisible}
              onPress={() => setIsSsnVisible(prev => !prev)}
            />
          }
        />

        <VnTextInput
          name="medicaidSubscriberId"
          control={control}
          labelI18nKey="native:addHouseholdMemberScreen.coverageCheck.medicaidId"
          placeholderI18nKey="native:addHouseholdMemberScreen.coverageCheck.medicaidIdPlaceholder"
          isRequired={false}
          returnKeyType="done"
          ref={medicaidSubscriberIdRef}
        />
      </VnBox>
    </>
  );
};
